
import { Modal } from "../../../evo/bootstrap";
import { MessageCircleQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldError, ModalFooter, ModalHeader, Required } from "@/components/admin/cards";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor as ClassicEditorBase } from '../../Helper/ckeditor';
import { sanitizeHtml, sanitizeCKEditorOnReady } from "../../Helper/sanitize";
import "../../assets/chatbotconfig.css"
import { useEffect } from 'react';

const QnAModal = ({
    setIsDirty,
    isDirty,
    show,
    onHide,
    mode, // "add" or "edit"
    loading,
    draft,
    setDraft,
    onSubmit,
    errorMsg,
    setErrorMsg,
    setPublishModal
}: any) => {

    // Warn before browser/tab close if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = ''; // Required for Chrome
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);
    const handleClose = () => {
        if (isDirty) {
            // If setPublishModal is provided, use it for confirmation dialog
            if (setPublishModal && typeof setPublishModal === 'function') {
                setPublishModal({
                    show: true,
                    title: "Unsaved Changes",
                    label: "You have unsaved changes. Do you want to leave without saving ?",
                    confirmText: "Yes, Discard",
                    cancelText: "Stay",
                    onConfirm: () => {
                        setIsDirty(false);
                        onHide(); // ✅ Close QnA modal
                        setPublishModal(prev => ({ ...prev, show: false }));
                    },
                    onClose: () => {
                        setPublishModal(prev => ({ ...prev, show: false }));
                    }
                });
                return;
            } else {
                // Fallback: Use browser confirm dialog if setPublishModal is not available
                const confirmed = window.confirm("You have unsaved changes. Do you want to leave without saving?");
                if (confirmed) {
                    setIsDirty(false);
                    onHide();
                }
                return;
            }
        }
        setIsDirty(false);
        onHide();
    };


    return (
        <Modal show={show} onHide={handleClose} centered size="lg" enforceFocus={false} backdrop="static"
            keyboard={false} aria-labelledby="qnaModalTitle">
            <ModalHeader
                icon={<MessageCircleQuestion />}
                title={<span id="qnaModalTitle">{mode === "edit" ? "Edit" : "Add"} FAQ</span>}
                description="Write the question as a guest would ask it, then the answer the chatbot should give."
                onClose={handleClose}
            />
            <Modal.Body className="position-relative">
                {loading && (
                    <div className="loaderOverlaySection zIndex-1023"><div className="loading"><div className="main-loader"></div></div></div>
                )}
                <div className={loading ? "blur-sm pointer-events-none" : ""}>
                    <div data-evo-island className="mb-4 text-foreground">
                        <Field label="User Question" htmlFor="qnaQuestion" required error={errorMsg?.question}>
                            <Input
                                id="qnaQuestion"
                                placeholder="e.g. What time is check-in?"
                                value={draft.question}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDraft({ ...draft, question: value });
                                    setIsDirty(true)
                                    // ✅ Clear error when valid input starts
                                    if (errorMsg?.question && value.trim()) {
                                        setErrorMsg((prev) => ({ ...prev, question: '' }));
                                    }

                                }}
                            />
                        </Field>
                    </div>
                    <div className="mb-3">
                        <p data-evo-island className="mb-1.5 text-sm font-medium text-foreground">Bot Response<Required /></p>
                        <div className="custom-editor-wrapper">
                            <CKEditor
                                editor={ClassicEditorBase}
                                data={sanitizeHtml(draft.answer || '')}
                                onReady={sanitizeCKEditorOnReady}
                                onChange={(event, editor) => {
                                    const value = editor.getData();
                                    setDraft({ ...draft, answer: value });
                                    setIsDirty(true)
                                    // ✅ Clear error when valid input starts
                                    if (errorMsg?.answer && value.trim()) {
                                        setErrorMsg((prev) => ({ ...prev, answer: '' }));
                                    }
                                }}
                                config={{
                                    toolbar: [
                                        'heading', '|', 'bold', 'italic', 'link', 'bulletedList',
                                        'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo'
                                    ],
                                    link: {
                                        decorators: {
                                            openInNewTab: {
                                                mode: 'automatic',
                                                callback: () => true,
                                                attributes: {
                                                    target: '_blank',
                                                    rel: 'noopener noreferrer',
                                                },
                                            },
                                        },
                                    },
                                }}
                            />
                        </div>
                        {errorMsg?.answer && (
                            <div data-evo-island className="mt-1.5"><FieldError message={errorMsg.answer} /></div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            {mode !== "view" && (
                <ModalFooter>
                    <Button type="button" variant="outline" onClick={handleClose}>Close</Button>
                    <Button type="button" onClick={onSubmit}>
                        {mode === "edit" ? "Update" : "Save"}
                    </Button>
                </ModalFooter>
            )}
        </Modal>
    );
}

export default QnAModal;
